import React, { useState, useEffect, useMemo } from 'react';
import { FunnelLayout } from '../../components/FunnelLayout';
import { SoWhatCallout } from '../../components/funnel/SoWhatCallout';
import { FunnelImage } from '../../components/funnel/FunnelImage';
import { useForm } from '../../context/FormContext';
import { useAppConfig } from '../../context/AppConfigContext';
import { BodyCondition } from '../../types';
import { getAvgWeightKg, getPuppyWeightKg } from '../../utils/breedMeta';
import { AgeCategory } from '../../types';

const OUT_OF_RANGE_THRESHOLD = 0.25;
const PESO_MIN_KG = 1;
// Alineado con el backend (schemas/dog.py: peso le=110) — un valor mayor
// pasaba P4 y P9 pero reventaba /calculate-plan con 422 permanente en Step11.
const PESO_MAX_KG = 110;
const SLIDER_MIN_KG = 0.5;
const SLIDER_MAX_KG = 80;
const DEFAULT_PESO_KG = 15;

type BodyConditionOption = {
  key: BodyCondition;
  labelKey: string;
  label: string;
  descKey: string;
  desc: string;
};

const BODY_CONDITIONS: BodyConditionOption[] = [
  {
    key: BodyCondition.DELGADO,
    labelKey: 'p4.body_delgado',
    label: 'Delgado',
    descKey: 'p4.body_delgado_desc',
    desc: 'Costillas visibles',
  },
  {
    key: BodyCondition.IDEAL,
    labelKey: 'p4.body_ideal',
    label: 'Ideal',
    descKey: 'p4.body_ideal_desc',
    desc: 'Cintura visible',
  },
  {
    key: BodyCondition.SOBREPESO,
    labelKey: 'p4.body_sobrepeso',
    label: 'Sobrepeso',
    descKey: 'p4.body_sobrepeso_desc',
    desc: 'Sin cintura',
  },
];

function clampPeso(value: number): number {
  if (!Number.isFinite(value)) return PESO_MIN_KG;
  return Math.min(PESO_MAX_KG, Math.max(PESO_MIN_KG, value));
}

function roundToTenth(value: number): number {
  return Math.round(value * 10) / 10;
}

function parsePesoInput(raw: string): number | null {
  const normalized = raw.replace(',', '.').trim();
  if (normalized === '') return null;
  const parsed = parseFloat(normalized);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
}

function P4Peso(): React.JSX.Element {
  const { formData, updateFormData, goToNextStep, goToPreviousStep } = useForm();
  const { config, t } = useAppConfig();
  const db = config.breed_database;

  const razaKey = formData.raza || '';
  const breedEntry = db?.breeds?.[razaKey];
  const breedDisplayName = breedEntry?.display_name ?? '';

  const isPuppy =
    formData.ageCategory === AgeCategory.CACHORRO ||
    (formData.ageTotalMonths !== undefined && formData.ageTotalMonths > 0 && formData.ageTotalMonths < 12);

  const [peso, setPesoState] = useState<number>(() => {
    if (formData.peso && formData.peso > 0) return clampPeso(formData.peso);
    if (isPuppy) {
      return clampPeso(getPuppyWeightKg(razaKey, db) ?? getAvgWeightKg(razaKey, db) ?? DEFAULT_PESO_KG);
    }
    return clampPeso(getAvgWeightKg(razaKey, db) ?? DEFAULT_PESO_KG);
  });

  const [pesoInputText, setPesoInputText] = useState<string>(() => String(peso));

  function setPeso(next: number | ((prev: number) => number)) {
    setPesoState((prev) => {
      const raw = typeof next === 'function' ? next(prev) : next;
      const clamped = clampPeso(roundToTenth(raw));
      setPesoInputText(String(clamped));
      return clamped;
    });
  }

  const avgWeight = useMemo(() => getAvgWeightKg(razaKey, db), [razaKey, db]);

  const [bodyCondition, setBodyCondition] = useState<BodyCondition | null>(() => formData.bodyCondition ?? null);

  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    updateFormData({ peso });
  }, [peso, updateFormData]);

  const weightMin = breedEntry?.weight_min_kg ?? null;
  const weightMax = breedEntry?.weight_max_kg ?? null;
  const showRango = breedDisplayName && weightMin !== null && weightMax !== null;

  const isOutOfRange = useMemo(() => {
    if (!razaKey || weightMin === null || weightMax === null) return false;
    const tooLow = peso < weightMin * (1 - OUT_OF_RANGE_THRESHOLD);
    const tooHigh = peso > weightMax * (1 + OUT_OF_RANGE_THRESHOLD);
    return tooLow || tooHigh;
  }, [peso, razaKey, weightMin, weightMax]);

  const bodyImages: Record<BodyCondition, string> = {
    [BodyCondition.DELGADO]:
      config.funnel_config.translations?.['p4.image.delgado'] || '/images/funnel/body-delgado.png',
    [BodyCondition.IDEAL]: config.funnel_config.translations?.['p4.image.ideal'] || '/images/funnel/body-ideal.png',
    [BodyCondition.SOBREPESO]:
      config.funnel_config.translations?.['p4.image.sobrepeso'] || '/images/funnel/body-sobrepeso.png',
  };

  const parsedPesoInput = parsePesoInput(pesoInputText);
  const inputPesoValid = parsedPesoInput !== null && parsedPesoInput >= PESO_MIN_KG && parsedPesoInput <= PESO_MAX_KG;
  const displayPeso = inputPesoValid ? roundToTenth(parsedPesoInput) : pesoInputText || '—';
  const canContinue = inputPesoValid && bodyCondition !== null;

  const nombre = formData.nombre || 'tu perro';

  const sidebarContent = bodyCondition ? (
    <SoWhatCallout>
      {bodyCondition === BodyCondition.SOBREPESO
        ? t(
            'p4.why_sobrepeso',
            'Con estos datos calcularemos las kcal diarias para que baje de peso de forma saludable.',
          )
        : bodyCondition === BodyCondition.DELGADO
          ? t('p4.why_delgado', 'Con estos datos calcularemos las kcal diarias para que coja peso de forma saludable.')
          : t('p4.why_ideal', 'Con estos datos calculamos las kcal diarias y los gramos de cada receta.')}
    </SoWhatCallout>
  ) : null;

  const missingReason = !inputPesoValid
    ? t('p4.missing_peso', 'Indica el peso de {nombre}', { nombre })
    : bodyCondition === null
      ? t('p4.missing_body', 'Selecciona la complexión')
      : undefined;

  function handlePesoStep(delta: number) {
    setPeso((prev) => prev + delta);
  }

  function handleSliderChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = parseFloat(e.target.value);
    if (Number.isFinite(next)) setPeso(next);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    if (!/^[0-9]*[.,]?[0-9]*$/.test(raw)) return;
    setPesoInputText(raw);
    const parsed = parsePesoInput(raw);
    if (parsed !== null && parsed >= PESO_MIN_KG && parsed <= PESO_MAX_KG) {
      setPesoState(parsed);
    }
  }

  function handleInputBlur() {
    const parsed = parsePesoInput(pesoInputText);
    if (parsed === null) {
      setPesoInputText(String(peso));
      return;
    }
    const clamped = clampPeso(roundToTenth(parsed));
    setPesoState(clamped);
    setPesoInputText(String(clamped));
  }

  function handleBodyConditionPick(key: BodyCondition) {
    setBodyCondition(key);
    updateFormData({ bodyCondition: key });
  }

  return (
    <FunnelLayout
      step={4}
      useTwoPhase
      onContinue={goToNextStep}
      continueDisabled={!canContinue}
      continueDisabledReason={missingReason}
      onContinueDisabledAttempt={() => setAttempted(true)}
      onBack={goToPreviousStep}
      sidebar={sidebarContent}
    >
      <div className="flex flex-col gap-4">
        <h1 className="font-fredoka text-xl sm:text-2xl font-semibold text-center text-black">
          {t('p4.title', '¿Cuánto pesa {nombre}?', { nombre })}
        </h1>

        {razaKey && avgWeight !== null && !(formData.peso && formData.peso > 0) && (
          <p className="font-poppins text-xs text-center text-[#008BBC]/80">
            {t(
              'p4.prefill_hint',
              'Hemos puesto una estimación inicial para {breedName}. Ajústala si no encaja con {nombre}.',
              { breedName: breedDisplayName, nombre },
            )}
          </p>
        )}

        <div className="text-center">
          <div className="flex items-baseline justify-center gap-1">
            <span className="font-fredoka text-4xl font-bold text-[#008BBC]">{displayPeso}</span>
            <span className="font-poppins text-base font-semibold text-black/50">kg</span>
          </div>

          {showRango && (
            <p className="font-poppins text-xs text-black/50 mt-1">
              {t('p4.rango_tipico', 'Rango {breedName}: {min}-{max} kg', {
                breedName: breedDisplayName,
                min: weightMin!,
                max: weightMax!,
              })}
            </p>
          )}
        </div>

        <div className="px-1">
          <input
            type="range"
            min={SLIDER_MIN_KG}
            max={SLIDER_MAX_KG}
            step={0.5}
            value={Math.min(SLIDER_MAX_KG, Math.max(SLIDER_MIN_KG, peso))}
            onChange={handleSliderChange}
            aria-label={t('p4.slider_aria', 'Peso de {nombre} en kg', { nombre })}
            aria-valuetext={`${peso} kg`}
            className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer slider-p4-thumb"
          />
          <div className="flex justify-between font-poppins text-[10px] text-black/40 mt-1">
            <span>{t('p4.peso_min', '0.5 kg')}</span>
            <span>{t('p4.peso_max', '80 kg')}</span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => handlePesoStep(-1)}
            className="w-9 h-9 flex items-center justify-center bg-[#90D1E8] rounded-[10px] text-white text-xl font-bold transition-all hover:opacity-80"
            aria-label={t('p4.peso_decrease_aria', 'Reducir peso')}
          >
            −
          </button>
          <input
            data-testid="peso-input"
            type="text"
            inputMode="decimal"
            pattern="[0-9]*[.,]?[0-9]*"
            autoComplete="off"
            value={pesoInputText}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            aria-label={t('p4.peso_input_aria', 'Peso en kg')}
            aria-invalid={!inputPesoValid}
            className={`no-spinner w-24 px-2 py-1.5 text-center font-fredoka text-lg font-bold border-2 rounded-[10px] focus:outline-none transition-all ${
              inputPesoValid ? 'border-gray-200 focus:border-[#008BBC]' : 'border-amber-400 focus:border-amber-500'
            }`}
          />
          <button
            type="button"
            onClick={() => handlePesoStep(1)}
            className="w-9 h-9 flex items-center justify-center bg-[#90D1E8] rounded-[10px] text-white text-xl font-bold transition-all hover:opacity-80"
            aria-label={t('p4.peso_increase_aria', 'Aumentar peso')}
          >
            +
          </button>
        </div>

        {!inputPesoValid && (
          <p className="text-center font-poppins text-xs text-amber-700">
            {t('p4.weight_invalid', 'Introduce un peso entre {min} y {max} kg.', {
              min: PESO_MIN_KG,
              max: PESO_MAX_KG,
            })}
          </p>
        )}

        {isOutOfRange && (
          <div className="bg-amber-50 border border-amber-300 rounded-[14px] px-4 py-3">
            <p className="font-poppins text-sm text-amber-800">
              {t('p4.out_of_range', 'Este peso está fuera del rango típico de la raza. Quizá quieras revisarlo.')}
            </p>
          </div>
        )}

        <div className="h-px bg-gray-200" />

        <div
          className={`flex flex-col gap-2 rounded-[22px] transition-all ${
            attempted && bodyCondition === null ? 'ring-2 ring-red-500 ring-offset-2 ring-offset-white p-3' : ''
          }`}
        >
          <h2 className="font-fredoka text-base sm:text-lg font-semibold text-center text-black">
            {t('p4.body_title', '¿Qué complexión tiene {nombre}?', { nombre })}
          </h2>

          <div className="grid grid-cols-3 gap-2">
            {BODY_CONDITIONS.map((bc) => {
              const isActive = bodyCondition === bc.key;
              return (
                <button
                  key={bc.key}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => handleBodyConditionPick(bc.key)}
                  className={`relative flex flex-col items-center justify-center py-3 px-2 rounded-[20px] transition-all duration-200 ${
                    isActive
                      ? 'bg-white border-[2.5px] border-[#008BBC] shadow-[0_0_0_4px_rgba(0,139,188,0.15)] scale-[1.03]'
                      : 'bg-white/70 border-2 border-transparent hover:border-[#90D1E8] hover:scale-105 hover:shadow-md'
                  }`}
                >
                  <FunnelImage
                    src={bodyImages[bc.key]}
                    alt={bc.label}
                    className="w-20 h-20 sm:w-24 sm:h-24 object-contain mb-2"
                  />
                  <span className={`font-fredoka text-sm font-medium ${isActive ? 'text-[#008BBC]' : 'text-black'}`}>
                    {t(bc.labelKey, bc.label)}
                  </span>
                  <span
                    className={`font-poppins text-[10px] mt-0.5 text-center ${
                      isActive ? 'text-[#008BBC]/70' : 'text-black/50'
                    }`}
                  >
                    {t(bc.descKey, bc.desc)}
                  </span>
                  {isActive && (
                    <div
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#008BBC] flex items-center justify-center shadow z-20"
                      aria-hidden="true"
                    >
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {bodyCondition && (
          <div className="lg:hidden">
            <SoWhatCallout>
              {bodyCondition === BodyCondition.SOBREPESO
                ? t(
                    'p4.why_sobrepeso',
                    'Con estos datos calcularemos las kcal diarias para que baje de peso de forma saludable.',
                  )
                : bodyCondition === BodyCondition.DELGADO
                  ? t(
                      'p4.why_delgado',
                      'Con estos datos calcularemos las kcal diarias para que coja peso de forma saludable.',
                    )
                  : t('p4.why_ideal', 'Con estos datos calculamos las kcal diarias y los gramos de cada receta.')}
            </SoWhatCallout>
          </div>
        )}
      </div>

      <style>{`
        .slider-p4-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 22px;
          height: 22px;
          background: #90D1E8;
          border: 3px solid white;
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          transition: all 0.2s ease;
        }
        .slider-p4-thumb::-webkit-slider-thumb:hover {
          transform: scale(1.15);
          box-shadow: 0 3px 10px rgba(0,0,0,0.3);
        }
        .slider-p4-thumb::-moz-range-thumb {
          width: 22px;
          height: 22px;
          background: #90D1E8;
          border: 3px solid white;
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
      `}</style>
    </FunnelLayout>
  );
}

export default P4Peso;
